import { RSAA } from 'redux-api-middleware';
import { buildRentCreditReportUrl, buildRenterPaymentsUrlV2, checkStatus, GET, POST } from '@common/paymentsUtils/api';
import {
  REQUEST_CANCEL_SCHEDULED_PAYMENT,
  REQUEST_CREATE_MANUAL_PAYMENT,
  REQUEST_CREATE_RENTER,
  REQUEST_CREDIT_TRACKING_UNENROLL,
  REQUEST_DELETE_PAYMENT_METHOD,
  REQUEST_ENROLL_RENTER_IN_CREDIT_TRACKING,
  REQUEST_GET_FEATURE_PERMISSIONS,
  REQUEST_INITIATE_PAYMENT,
  REQUEST_LINK_TOKEN,
  REQUEST_MARK_ACKNOWLEDGED,
  REQUEST_MOVE_IN_COSTS_PAYMENT,
  REQUEST_PAY_ONE_TIME_PAYMENT,
  REQUEST_PAYMENT_REPORTING_HISTORY,
  REQUEST_PAYMENT_REPORTING_OPT_IN,
  REQUEST_PAYMENT_REPORTING_OPT_OUT,
  REQUEST_PAYMENT_REPORTING_STATUS,
  REQUEST_PAYMENT_REPORTING_STATUS_WITH_CREDIT_TRACKING,
  REQUEST_RENT_DETAILS,
  REQUEST_RENTER_STATUS,
  REQUEST_UPDATE_MODE_LINK_TOKEN,
  REQUEST_USER_API,
} from '@renter-hub/constants/actionTypes';
import RenterStatusModel from '@renter-hub/models/RenterStatus';
import FeaturePermissionModel from '@renter-hub/models/FeaturePermission';
import ConfigModel from '@renter-hub/models/Config';
import { MoveInCostsModel } from '@renter-hub/models/MoveInCosts';
import { formatTimestampToYMDString, formatYMDStringToTimestamp } from '@common/paymentsUtils/dateUtils';
import StringConstants from '@common/constants/StringConstants';
import type {
  RequestCreateManualPaymentInterface,
  RequestCreateRenterAccountInterface,
  RequestInitiatePaymentInterface,
  RequestMoveInCostsPaymentInterface,
  RequestPaymentReportingOptInInterface,
  RequestPayOneTimePaymentInterface,
} from '@renter-hub/models/RequestsTypes';

const renterHubProxy = StringConstants.API.RENTER_HUB.PROXY;
const renterHubApi = StringConstants.API.RENTER_HUB.API;
const renterHubApiV1 = renterHubProxy + renterHubApi;
const requestHeader = {
  'Content-Type': 'application/json',
};

type RequestResponse = {
  [x: number]: Record<string, unknown>;
};

export const getUserRenter = ({ requireStrongAuthentication }): RequestResponse => ({
  [RSAA]: {
    endpoint: `${renterHubApiV1}/get?pageWithSensitiveInformation=${requireStrongAuthentication}`,
    method: GET,
    headers: requestHeader,
    credentials: 'same-origin',
    types: [{ type: REQUEST_USER_API.INIT }, { type: REQUEST_USER_API.SUCCESS }, { type: REQUEST_USER_API.FAIL }],
  },
});

export const requestRenterStatus = (encryptedInvite = ''): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/getRenterStatus'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({ encryptedInvite }),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_RENTER_STATUS.INIT,
      {
        type: REQUEST_RENTER_STATUS.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json: any = {}) => ({
              renterStatus: RenterStatusModel(json.data),
              config: ConfigModel(json.config),
              httpStatus: json.httpStatus,
            }))
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_RENTER_STATUS.FAIL,
    ],
  },
});

export const requestRentDetailsFromInvite = (encryptedInvite: string): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/getRentDetailsFromInvite'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({ encryptedInvite }),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_RENT_DETAILS.INIT,
      {
        type: REQUEST_RENT_DETAILS.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => {
              const { data } = json;
              const { firstDueDate, lastDueDate, moveInCosts } = data;

              return {
                ...data,
                firstDueDate: formatTimestampToYMDString(firstDueDate),
                lastDueDate: formatTimestampToYMDString(lastDueDate),
                moveInCosts: MoveInCostsModel(moveInCosts),
              };
            })
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_RENT_DETAILS.FAIL,
    ],
  },
});

export const requestInitiatePayment = (paymentDetails: RequestInitiatePaymentInterface): RequestResponse => {
  const { paymentDate } = paymentDetails;
  const newPaymentDetails = {
    ...paymentDetails,
    paymentDate: formatYMDStringToTimestamp(paymentDate),
  };
  return {
    [RSAA]: {
      endpoint: buildRenterPaymentsUrlV2('/createPayment'),
      method: POST,
      headers: requestHeader,
      body: JSON.stringify(newPaymentDetails),
      credentials: 'same-origin', // add cookies to requests of same origin
      types: [
        REQUEST_INITIATE_PAYMENT.INIT,
        {
          type: REQUEST_INITIATE_PAYMENT.SUCCESS,
          payload: (action, state, res) =>
            res
              .json()
              .then(checkStatus)
              .then((json) => json.data)
              .catch((json) => ({
                error: true,
                originalResponse: json,
              })),
        },
        REQUEST_INITIATE_PAYMENT.FAIL,
      ],
    },
  };
};

export const requestCreateManualPayment = (
  manualPaymentDetails: RequestCreateManualPaymentInterface,
): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/createManualPayment'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify(manualPaymentDetails),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_CREATE_MANUAL_PAYMENT.INIT,
      {
        type: REQUEST_CREATE_MANUAL_PAYMENT.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.data)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_CREATE_MANUAL_PAYMENT.FAIL,
    ],
  },
});

export const requestCancelScheduledPayment = (subscriptionType: string): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/cancelPayment'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({ type: subscriptionType }),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_CANCEL_SCHEDULED_PAYMENT.INIT,
      {
        type: REQUEST_CANCEL_SCHEDULED_PAYMENT.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.data)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_CANCEL_SCHEDULED_PAYMENT.FAIL,
    ],
  },
});

// useAcceptRentAmountChangeWebApiHandler_acceptRentAmountChange({ postData: { acceptExpiration: true } });

export const requestCreateRenterAccount = (renterInfo: RequestCreateRenterAccountInterface): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/createRenter'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify(renterInfo),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_CREATE_RENTER.INIT,
      {
        type: REQUEST_CREATE_RENTER.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.data)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_CREATE_RENTER.FAIL,
    ],
  },
});

export const deletePaymentMethod = (encodedPaymentMethodId: string): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/deletePaymentMethod'),
    method: POST,
    headers: requestHeader,
    body: (state: Record<string, any>): string =>
      JSON.stringify({
        userId: state.user.userToken,
        authZone: 'zillow',
        encodedPaymentMethodId,
      }),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_DELETE_PAYMENT_METHOD.INIT,
      {
        type: REQUEST_DELETE_PAYMENT_METHOD.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.data)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_DELETE_PAYMENT_METHOD.FAIL,
    ],
  },
});

export const requestPayOneTimePayment = (paymentInfo: RequestPayOneTimePaymentInterface): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/payOneTimePayment'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify(paymentInfo),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_PAY_ONE_TIME_PAYMENT.INIT,
      {
        type: REQUEST_PAY_ONE_TIME_PAYMENT.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.data)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_PAY_ONE_TIME_PAYMENT.FAIL,
    ],
  },
});

export const requestMoveInCostsPayment = (
  moveInCostsPaymentInfo: RequestMoveInCostsPaymentInterface,
): RequestResponse => {
  const { paymentDate } = moveInCostsPaymentInfo;
  const newMoveInCostsPaymentInfo = {
    ...moveInCostsPaymentInfo,
    paymentDate: formatYMDStringToTimestamp(paymentDate),
  };
  return {
    [RSAA]: {
      endpoint: buildRenterPaymentsUrlV2('/scheduleMoveInCosts'),
      method: POST,
      headers: requestHeader,
      body: JSON.stringify(newMoveInCostsPaymentInfo),
      credentials: 'same-origin', // add cookies to requests of same origin
      types: [
        REQUEST_MOVE_IN_COSTS_PAYMENT.INIT,
        {
          type: REQUEST_MOVE_IN_COSTS_PAYMENT.SUCCESS,
          payload: (action, state, res) =>
            res
              .json()
              .then(checkStatus)
              .then((json) => json.data)
              .catch((json) => ({
                error: true,
                originalResponse: json,
              })),
        },
        REQUEST_MOVE_IN_COSTS_PAYMENT.FAIL,
      ],
    },
  };
};

export const requestMarkAcknowledged = (actionId): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/markAcknowledged'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({ actionId }),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_MARK_ACKNOWLEDGED.INIT,
      {
        type: REQUEST_MARK_ACKNOWLEDGED.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.data)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_MARK_ACKNOWLEDGED.FAIL,
    ],
  },
});

export const getPlaidLinkToken = ({ propertyId }): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/createLinkToken'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_LINK_TOKEN.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_LINK_TOKEN.SUCCESS,
        meta: { propertyId },
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: ConfigModel(json.data),
          })),
      },
      {
        type: REQUEST_LINK_TOKEN.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const requestUpdateModeLinkToken = (externalId?: string): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/createUpdateModeLinkToken'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      ...(externalId && { externalId }),
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_UPDATE_MODE_LINK_TOKEN.INIT,
        meta: { externalId },
      },
      {
        type: REQUEST_UPDATE_MODE_LINK_TOKEN.SUCCESS,
        meta: { externalId },
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: ConfigModel(json.data),
          })),
      },
      {
        type: REQUEST_UPDATE_MODE_LINK_TOKEN.FAIL,
        meta: { externalId },
      },
    ],
  },
});

export const getFeaturePermissions = ({ propertyId }): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/getFeaturePermissions'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_GET_FEATURE_PERMISSIONS.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_GET_FEATURE_PERMISSIONS.SUCCESS,
        meta: { propertyId },
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: FeaturePermissionModel(json.data),
          })),
      },
      {
        type: REQUEST_GET_FEATURE_PERMISSIONS.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const requestPaymentReportingOptIn = (renterInfo: RequestPaymentReportingOptInInterface): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRentCreditReportUrl('/optIn'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify(renterInfo),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_PAYMENT_REPORTING_OPT_IN.INIT,
        meta: { renterInfo },
      },
      {
        type: REQUEST_PAYMENT_REPORTING_OPT_IN.SUCCESS,
        meta: { renterInfo },

        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then(() => ({
              optedIn: true,
              optInDate: new Date().toLocaleDateString(),
            }))
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      {
        type: REQUEST_PAYMENT_REPORTING_OPT_IN.FAIL,
        meta: { renterInfo },
      },
    ],
  },
});

export const requestPaymentReportingStatus = (rentTermsKey: string): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRentCreditReportUrl('/getOptIn'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({ rentTermsKey }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_PAYMENT_REPORTING_STATUS.INIT,
        meta: { rentTermsKey },
      },
      {
        type: REQUEST_PAYMENT_REPORTING_STATUS.SUCCESS,
        meta: { rentTermsKey },
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.response)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      {
        type: REQUEST_PAYMENT_REPORTING_STATUS.FAIL,
        meta: { rentTermsKey },
      },
    ],
  },
});

export const requestPaymentReportingHistory = (rentTermsKey: string): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRentCreditReportUrl('/getPaymentReportHistory'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({ rentTermsKey }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_PAYMENT_REPORTING_HISTORY.INIT,
        meta: { rentTermsKey },
      },
      {
        type: REQUEST_PAYMENT_REPORTING_HISTORY.SUCCESS,
        meta: { rentTermsKey },
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.response)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      {
        type: REQUEST_PAYMENT_REPORTING_HISTORY.FAIL,
        meta: { rentTermsKey },
      },
    ],
  },
});

export const requestPaymentReportingOptOut = (rentTermsKey: string): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRentCreditReportUrl('/optOut'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({ rentTermsKey }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_PAYMENT_REPORTING_OPT_OUT.INIT,
        meta: { rentTermsKey },
      },
      {
        type: REQUEST_PAYMENT_REPORTING_OPT_OUT.SUCCESS,
        meta: { rentTermsKey },
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.response)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      {
        type: REQUEST_PAYMENT_REPORTING_OPT_OUT.FAIL,
        meta: { rentTermsKey },
      },
    ],
  },
});

export const requestEnrollRenterInCreditTracking = (arrayUserId: string): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRentCreditReportUrl(`/enrollRenterInCreditTracking?arrayUserId=${arrayUserId}`),
    method: GET,
    headers: requestHeader,
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_ENROLL_RENTER_IN_CREDIT_TRACKING.INIT,
        meta: { arrayUserId },
      },
      {
        type: REQUEST_ENROLL_RENTER_IN_CREDIT_TRACKING.SUCCESS,
        meta: { arrayUserId },
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.response)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      {
        type: REQUEST_ENROLL_RENTER_IN_CREDIT_TRACKING.FAIL,
        meta: { arrayUserId },
      },
    ],
  },
});

export const requestUnenrollRenterInCreditTracking = (): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRentCreditReportUrl('/unenrollRenterFromCreditTracking'),
    method: GET,
    headers: requestHeader,
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_CREDIT_TRACKING_UNENROLL.INIT,
      },
      {
        type: REQUEST_CREDIT_TRACKING_UNENROLL.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.response)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      {
        type: REQUEST_CREDIT_TRACKING_UNENROLL.FAIL,
      },
    ],
  },
});

export const requestPaymentReportingStatusWithCreditTracking = (rentTermsKey: string): RequestResponse => {
  return {
    [RSAA]: {
      endpoint: buildRentCreditReportUrl('/getOptInWithCreditTracking'),
      method: POST,
      body: JSON.stringify({ rentTermsKey }),
      headers: {
        ...requestHeader,
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      types: [
        {
          type: REQUEST_PAYMENT_REPORTING_STATUS_WITH_CREDIT_TRACKING.INIT,
          meta: { rentTermsKey },
        },
        {
          type: REQUEST_PAYMENT_REPORTING_STATUS_WITH_CREDIT_TRACKING.SUCCESS,
          meta: { rentTermsKey },
          payload: (action, state, res) =>
            res
              .json()
              .then(checkStatus)
              .then((json) => ({
                optedIn: json.response.optedIn,
                optInDate: json.response.optInDate,
                creditTracking: json.response.creditTracking,
              }))
              .catch((json) => ({
                error: true,
                originalResponse: json,
              })),
        },
        {
          type: REQUEST_PAYMENT_REPORTING_STATUS_WITH_CREDIT_TRACKING.FAIL,
          meta: { rentTermsKey },
        },
      ],
    },
  };
};

/**
 * Execute >1 requests in parallel
 * Use this when feature permissions may have changed, as well as renter status.
 */
export const getRachelDashboard =
  ({
    encryptedInvite = '',

    propertyId,
  }: {
    encryptedInvite?: string;
    propertyId?: string;
  }) =>
  async (dispatch) =>
    Promise.all([
      await dispatch(requestRenterStatus(encryptedInvite)),
      // await dispatch(getFeaturePermissions({ propertyId })),
    ]);
